import type { TemplateStudioDraft } from "@/lib/template-studio/draft/templateStudioDraft";
import type {
  TemplateLayoutRepository,
  TemplateLayoutRepositoryContext,
  TemplateLayoutRepositoryResult,
} from "@/lib/template-studio/repository/templateLayoutRepository.types";
import type { TemplateLayout } from "@/types/template-layout/templateLayout.types";
import type {
  TemplateStudioSavedTemplateRecord,
  TemplateStudioSaveMetadata,
} from "./templateStudioSaveState";

export interface SaveTemplateStudioDraftInput {
  repository: TemplateLayoutRepository;
  context: TemplateLayoutRepositoryContext;
  draft: TemplateStudioDraft;
  metadata: TemplateStudioSaveMetadata;
  savedTemplate: TemplateStudioSavedTemplateRecord | null;
}

/** @public */
export const normalizeTemplateStudioSaveMetadata = (
  metadata: TemplateStudioSaveMetadata,
): TemplateStudioSaveMetadata => {
  const name = metadata.name.trim();
  const description = metadata.description?.trim();

  return {
    name,
    ...(description !== undefined ? { description } : {}),
  };
};

/** @public */
export const isTemplateStudioSaveMetadataValid = (
  metadata: TemplateStudioSaveMetadata,
): boolean => normalizeTemplateStudioSaveMetadata(metadata).name.length > 0;

const validateTemplateStudioSaveMetadata = (
  metadata: TemplateStudioSaveMetadata,
): TemplateLayoutRepositoryResult<TemplateLayout> | null => {
  if (isTemplateStudioSaveMetadataValid(metadata)) return null;

  return {
    success: false,
    error: {
      code: "INVALID_DOCUMENT",
      message: "Template name is required.",
      validationErrors: [
        {
          path: "name",
          message: "Template name is required.",
        },
      ],
    },
  };
};

/**
 * Persists the current draft through the repository boundary.
 * Creates on first save and updates with `expectedUpdatedAt` afterward, unless
 * briefings now use the template.
 * @public
 */
export const saveTemplateStudioDraft = async (
  input: SaveTemplateStudioDraftInput,
): Promise<TemplateLayoutRepositoryResult<TemplateLayout>> => {
  const metadata = normalizeTemplateStudioSaveMetadata(input.metadata);
  const metadataValidation = validateTemplateStudioSaveMetadata(metadata);
  if (metadataValidation != null) return metadataValidation;

  const { repository, context, draft, savedTemplate } = input;

  if (savedTemplate == null) {
    return repository.create(context, {
      name: metadata.name,
      ...(metadata.description ? { description: metadata.description } : {}),
      document: draft,
    });
  }

  // The backend doesn't block updating a template that briefings use, so check
  // it here in case a briefing picked it up after Template Studio opened it.
  // The check is advisory: if the read fails, update anyway, still guarded by
  // `expectedUpdatedAt`.
  const current = await repository.get(context, savedTemplate.id);
  if (current.success && current.data.briefingCount > 0) {
    return {
      success: false,
      error: {
        code: "TEMPLATE_IN_USE",
        message: "Briefings use this template.",
      },
    };
  }

  return repository.update(context, {
    id: savedTemplate.id,
    name: metadata.name,
    description: metadata.description,
    document: draft,
    expectedUpdatedAt: savedTemplate.updatedAt,
  });
};
